// ============================================================
// Pierian Lead Scoring Engine
// Pure scoring logic — no UI, no side effects, fully testable
// ============================================================

import type {
  Lead, ScoringConfig, ScoreResult, ScoredLead,
  DimensionKey, FieldConfigMap, TierNumber, TierLabel,
  DimensionWeights, TierThresholds,
} from './types';

// ── Default Configuration ──────────────────────────────────

export const DEFAULT_FIELD_CONFIG: FieldConfigMap = {
  readiness: {
    label: 'Readiness',
    fields: {
      dmAccess:            { label: 'Do we have access to the Decision Maker?', active: true, weight: 20, mapping: 'Yes=10 | Partially or through a partner=5 | No=0' },
      dmRole:              { label: 'Who is the Decision Maker?',               active: true, weight: 20, mapping: 'CEO=10 | CFO=10 | CDO=9 | COO=9 | CTO=8 | Other C-suite=8 | VP=6 | Director=4' },
      pastDelivery:        { label: 'Have we delivered this service in the past?', active: true, weight: 20, mapping: 'Yes=10 | Partially or through a partner=6 | No=3' },
      skillAvailable:      { label: 'Do we have Skills Available?',             active: true, weight: 12, mapping: 'Yes=10 | Partially or through a partner=5 | No=0' },
      techCapability:      { label: 'Do we have Technology Capability?',        active: true, weight: 12, mapping: 'Yes=10 | Partially or through a partner=5 | No=0' },
      locationFeasibility: { label: 'Do we operate in this Location?',          active: true, weight: 12, mapping: 'Yes=10 | Partially or through a partner=5 | No=0' },
      locationSetup:       { label: 'Can we set up delivery for this Location?', active: true, weight: 4,  mapping: 'Yes=10 | Partially or through a partner=5 | No=0' },
    },
  },
  dealEconomics: {
    label: 'Deal Economics',
    fields: {
      dealACV:  { label: 'Deal Size ACV (USD)', active: true, weight: 40, mapping: '≥$2M=10 | $1-2M=8 | $500K-1M=6 | <$500K=3' },
      dealTCV:  { label: 'Deal Size TCV (USD)', active: true, weight: 40, mapping: '≥$5M=10 | $2-5M=8 | $1-2M=6 | <$1M=3' },
      timeline: { label: 'Timeline',            active: true, weight: 20, mapping: '<3mo=10 | 3-6mo=7.5 | 6-12mo=5 | >12mo=2' },
    },
  },
  leadSource: {
    label: 'Lead Source',
    fields: {
      leadSource: { label: 'Lead Source', active: true, weight: 100, mapping: 'Referral=10 | Partner=9 | Inbound (Web)=8 | Inbound (Content)=8 | Event=7 | LinkedIn=6 | Email Campaign=5 | Website=5 | Outbound=4 | Social Media=3 | Paid Ads=3 | Cold Call=2 | Other=1' },
    },
  },
};

export const DEFAULT_WEIGHTS: DimensionWeights = {
  readiness: 50,
  dealEconomics: 35,
  leadSource: 15,
};

export const DEFAULT_THRESHOLDS: TierThresholds = {
  tier1: 8,
  tier2: 6,
  tier3: 4,
};

export function createDefaultConfig(): ScoringConfig {
  return {
    weights: { ...DEFAULT_WEIGHTS },
    thresholds: { ...DEFAULT_THRESHOLDS },
    fieldConfig: JSON.parse(JSON.stringify(DEFAULT_FIELD_CONFIG)),
  };
}

// ── Field Score Functions ──────────────────────────────────

const SCORE_MAPS = {
  dmAccess:            { 'Yes': 10, 'Partially or through a partner': 5, 'No': 0 } as Record<string, number>,
  dmRole:              { 'CEO': 10, 'CFO': 10, 'CDO': 9, 'COO': 9, 'CTO': 8, 'Other C-suite': 8, 'VP': 6, 'Director': 4, 'Head Of Internal Audit': 4, 'Others': 3 } as Record<string, number>,
  pastDelivery:        { 'Yes': 10, 'Partially or through a partner': 6, 'No': 3 } as Record<string, number>,
  skillAvailable:      { 'Yes': 10, 'Partially or through a partner': 5, 'No': 0 } as Record<string, number>,
  techCapability:      { 'Yes': 10, 'Partially or through a partner': 5, 'No': 0 } as Record<string, number>,
  locationFeasibility: { 'Yes': 10, 'Partially or through a partner': 5, 'No': 0 } as Record<string, number>,
  locationSetup:       { 'Yes': 10, 'Partially or through a partner': 5, 'No': 0 } as Record<string, number>,
  timeline:            { '<3 months': 10, '3-6 months': 7.5, '6-12 months': 5, '>12 months': 2 } as Record<string, number>,
  leadSource: {
    'Referral': 10, 'Partner': 9, 'Inbound (Web)': 8, 'Inbound (Content)': 8,
    'Event': 7, 'LinkedIn': 6, 'Email Campaign': 5, 'Website': 5,
    'Outbound': 4, 'Social Media': 3, 'Paid Ads': 3, 'Cold Call': 2, 'Other': 1,
  } as Record<string, number>,
};

export function fieldScore(fieldKey: string, value: string | number): number {
  // Numeric fields (ACV / TCV)
  if (fieldKey === 'dealACV') {
    const v = typeof value === 'string' ? parseFloat(value) || 0 : value;
    return v >= 2_000_000 ? 10 : v >= 1_000_000 ? 8 : v >= 500_000 ? 6 : v > 0 ? 3 : 0;
  }
  if (fieldKey === 'dealTCV') {
    const v = typeof value === 'string' ? parseFloat(value) || 0 : value;
    return v >= 5_000_000 ? 10 : v >= 2_000_000 ? 8 : v >= 1_000_000 ? 6 : v > 0 ? 3 : 0;
  }
  // Map-based fields
  const map = SCORE_MAPS[fieldKey as keyof typeof SCORE_MAPS];
  if (!map) return 0;
  return map[String(value)] ?? 0;
}

// ── Weighted Dimension Score ───────────────────────────────

export function weightedDimensionScore(
  fieldConfig: FieldConfigMap,
  dimKey: DimensionKey,
  fieldScores: Record<string, number>,
): number {
  const fields = fieldConfig[dimKey]?.fields ?? {};
  let totalWeight = 0;
  let weightedSum = 0;

  for (const [key, score] of Object.entries(fieldScores)) {
    const cfg = fields[key];
    if (!cfg || !cfg.active || cfg.weight <= 0) continue;
    weightedSum += score * cfg.weight;
    totalWeight += cfg.weight;
  }

  return totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;
}

// ── Dimension Calculators ──────────────────────────────────

export function calculateReadiness(lead: Lead, config: ScoringConfig): number {
  return weightedDimensionScore(config.fieldConfig, 'readiness', {
    dmAccess:            fieldScore('dmAccess', lead.dmAccess),
    dmRole:              fieldScore('dmRole', lead.dmRole),
    pastDelivery:        fieldScore('pastDelivery', lead.pastDelivery),
    skillAvailable:      fieldScore('skillAvailable', lead.skillAvailable),
    techCapability:      fieldScore('techCapability', lead.techCapability),
    locationFeasibility: fieldScore('locationFeasibility', lead.locationFeasibility),
    locationSetup:       fieldScore('locationSetup', lead.locationSetup),
  });
}

export function calculateDealEconomics(lead: Lead, config: ScoringConfig): number {
  return weightedDimensionScore(config.fieldConfig, 'dealEconomics', {
    dealACV:  fieldScore('dealACV', lead.dealACV),
    dealTCV:  fieldScore('dealTCV', lead.dealTCV),
    timeline: fieldScore('timeline', lead.timeline),
  });
}

export function calculateLeadSource(lead: Lead, config: ScoringConfig): number {
  return weightedDimensionScore(config.fieldConfig, 'leadSource', {
    leadSource: fieldScore('leadSource', lead.leadSource),
  });
}

// ── Composite Score & Tier ─────────────────────────────────

export function calculateCompositeScore(lead: Lead, config: ScoringConfig): ScoreResult {
  const readiness = calculateReadiness(lead, config);
  const deal      = calculateDealEconomics(lead, config);
  const source    = calculateLeadSource(lead, config);

  const composite =
    (readiness * config.weights.readiness / 100) +
    (deal * config.weights.dealEconomics / 100) +
    (source * config.weights.leadSource / 100);

  const rounded = Math.round(composite * 10) / 10;
  const tier    = getTier(rounded, config.thresholds);

  return {
    composite: rounded,
    readiness: Math.round(readiness * 10) / 10,
    deal:      Math.round(deal * 10) / 10,
    source:    Math.round(source * 10) / 10,
    tier,
    tierLabel: getTierLabel(tier),
  };
}

export function getTier(score: number, thresholds: TierThresholds): TierNumber {
  if (score >= thresholds.tier1) return '1';
  if (score >= thresholds.tier2) return '2';
  if (score >= thresholds.tier3) return '3';
  return '4';
}

export function getTierLabel(tier: TierNumber): TierLabel {
  const labels: Record<TierNumber, TierLabel> = {
    '1': 'PURSUE',
    '2': 'PIPELINE',
    '3': 'NURTURE',
    '4': 'DEPRIORITIZE',
  };
  return labels[tier];
}

// ── Batch Scoring ──────────────────────────────────────────

export function scoreLeads(leads: Lead[], config?: ScoringConfig): ScoredLead[] {
  const cfg = config ?? createDefaultConfig();
  return leads.map(lead => ({
    ...lead,
    scores: calculateCompositeScore(lead, cfg),
  }));
}

/** Score a single lead */
export function scoreLead(lead: Lead, config?: ScoringConfig): ScoredLead {
  const cfg = config ?? createDefaultConfig();
  return { ...lead, scores: calculateCompositeScore(lead, cfg) };
}

// ── Field Option Constants (for building forms) ────────────

export const FIELD_OPTIONS = {
  dmAccess:            ['Yes', 'Partially or through a partner', 'No'] as const,
  dmRole:              ['CEO', 'CFO', 'CDO', 'COO', 'CTO', 'Other C-suite', 'VP', 'Director', 'Head Of Internal Audit', 'Others'] as const,
  pastDelivery:        ['Yes', 'Partially or through a partner', 'No'] as const,
  skillAvailable:      ['Yes', 'Partially or through a partner', 'No'] as const,
  techCapability:      ['Yes', 'Partially or through a partner', 'No'] as const,
  locationFeasibility: ['Yes', 'Partially or through a partner', 'No'] as const,
  locationSetup:       ['Yes', 'Partially or through a partner', 'No'] as const,
  timeline:            ['<3 months', '3-6 months', '6-12 months', '>12 months'] as const,
  leadSource:          ['Referral', 'Partner', 'Inbound (Web)', 'Inbound (Content)', 'Event', 'LinkedIn', 'Email Campaign', 'Website', 'Outbound', 'Social Media', 'Paid Ads', 'Cold Call', 'Other'] as const,
};
